import React, { useMemo } from "react";
import "./EnumSelect.css";

// Enum型の基本構造を定義
export interface EnumOption {
  value: number;
  name: string;
  label?: string;
}

// ラベルフォーマット関数の型定義
export type LabelFormatter = (value: number, name: string) => string;

// 選択された値の情報
export interface SelectedEnumValue {
  value: number;
  name: string;
  label: string;
}

export interface EnumSelectProps {
  // Enumオブジェクトまたは変換済みのオプション配列
  enumObject?: Record<string, number | string>;
  options?: EnumOption[];
  // 現在選択されている値
  value?: number;
  // 値変更時のコールバック
  onChange?: (selected: SelectedEnumValue) => void;
  // ラベルフォーマット関数（デフォルト: "{value}: {name}"）
  labelFormatter?: LabelFormatter;
  // プレースホルダー
  placeholder?: string;
  // 無効化フラグ
  disabled?: boolean;
  // CSS クラス名
  className?: string;
  // UNSPECIFIED値を除外するかどうか
  excludeUnspecified?: boolean;
  // デバッグ情報を表示するかどうか
  showDebugInfo?: boolean;
}

// デフォルトのラベルフォーマット関数
const defaultLabelFormatter: LabelFormatter = (value: number, name: string) => {
  return `${value}: ${name}`;
};

// Enumオブジェクトをオプション配列に変換する関数
const convertEnumToOptions = (enumObject: Record<string, number | string>): EnumOption[] => {
  return Object.entries(enumObject)
    .filter(([name, value]) => {
      // 数値キー（逆マッピング）を除外し、文字列キーのみを処理
      return isNaN(Number(name)) && typeof value === 'number';
    })
    .map(([name, value]) => ({
      value: value as number,
      name,
    }))
    .sort((a, b) => a.value - b.value); // 値順でソート
};

export const EnumSelect: React.FC<EnumSelectProps> = ({
  enumObject,
  options: providedOptions,
  value,
  onChange,
  labelFormatter = defaultLabelFormatter,
  placeholder = "選択してください",
  disabled = false,
  className = "",
  excludeUnspecified = true,
  showDebugInfo = false,
}) => {
  // オプション配列を生成
  const options = useMemo(() => {
    let opts: EnumOption[];
    
    if (providedOptions) {
      opts = providedOptions;
    } else if (enumObject) {
      opts = convertEnumToOptions(enumObject);
    } else {
      opts = [];
    }

    // UNSPECIFIED値を除外する場合
    if (excludeUnspecified) {
      opts = opts.filter(opt => 
        !opt.name.toUpperCase().includes('UNSPECIFIED')
      );
    }

    // ラベルを生成
    return opts.map(opt => ({
      ...opt,
      label: opt.label || labelFormatter(opt.value, opt.name),
    }));
  }, [enumObject, providedOptions, labelFormatter, excludeUnspecified]);

  // 選択された値の情報を取得
  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value);
  }, [options, value]);

  // 値変更ハンドラー
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = parseInt(event.target.value, 10);
    const option = options.find(opt => opt.value === selectedValue);
    
    if (option && onChange) {
      onChange({
        value: option.value,
        name: option.name,
        label: option.label || labelFormatter(option.value, option.name),
      });
    }
  };

  return (
    <div className={`enum-select ${className}`}>
      <select
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled}
        className="enum-select__input"
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      {/* 選択された値の詳細情報（デバッグ用、必要に応じて非表示にできる） */}
      {showDebugInfo && selectedOption && (
        <div className="enum-select__selected-info">
          <small>
            選択値: {selectedOption.value} | 名前: {selectedOption.name}
          </small>
        </div>
      )}
    </div>
  );
};

// 便利な関数：enum名から自動的にラベルを生成
export const createEnumNameFormatter = (prefix?: string): LabelFormatter => {
  return (value: number, name: string) => {
    // プレフィックスを除去してより読みやすい名前に変換
    let displayName = name;
    if (prefix) {
      displayName = name.replace(new RegExp(`^${prefix}_?`, 'i'), '');
    }
    
    // アンダースコアをスペースに変換し、各単語の最初を大文字に
    displayName = displayName
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return `${value}: ${displayName}`;
  };
};

// 値のみを表示するフォーマッター
export const valueOnlyFormatter: LabelFormatter = (value: number) => {
  return value.toString();
};

// 名前のみを表示するフォーマッター
export const nameOnlyFormatter: LabelFormatter = (value: number, name: string) => {
  return name;
};
